/**
 * Practice inventory: items, stock per variation, the reorder list and barcode lookup.
 *
 * Mirrors /api/inventory on the REST API. Every call acts in Session.practice, except the two
 * that take a practiceId - looking up and listing in another practice - which the move flow
 * needs to find the variation the stock is going to. The server checks the caller is registered
 * with that practice too.
 *
 * Quantities are always single units. A pack is converted by the caller using the variation's
 * units_per_pack before it is sent.
 */
app.service("InventoryAPI", [
	"$http",
	"ENV",
	"Session",
	function ($http, ENV, Session) {
		const baseUri = ENV.API + "/api/inventory";

		/**
		 * Generates the common headers required for API authentication.
		 * @param {number} [practiceId] - The practice to act in. Defaults to the session practice.
		 * @returns {Object} The headers object.
		 */
		const getHeaders = function (practiceId) {
			return {
				Authorization: "Bearer " + Session.token,
				"X-PRACTICE-ID": `${practiceId || Session.practice.id}`,
			};
		};

		var service = {
			isProcessing: false,

			/**
			 * The statuses of an item, and of each of its variations, matching InventoryItemStatusType on
			 * the server. An archived item's variations are archived with it, whatever their own status.
			 */
			STATUS: {
				ACTIVE: "ACTIVE",
				ARCHIVED: "ARCHIVED",
			},

			/**
			 * The variations that can still be stocked and sold: none on an archived item, and none
			 * archived on their own. What to offer in a picker.
			 */
			activeVariations: function (item) {
				if (!item || item.status === service.STATUS.ARCHIVED) {
					return [];
				}
				return (item.variations || []).filter(v => v.status !== service.STATUS.ARCHIVED);
			},

			/**
			 * The categories, matching InventoryCategoryType on the server. OTHER items carry their
			 * own name in other_category; no other category does. SUNDRY is retired - offered nowhere,
			 * but kept so items already in it still show their category.
			 */
			CATEGORY: {
				FRAME: "FRAME",
				SUNGLASSES: "SUNGLASSES",
				CONTACT_LENSES: "CONTACT_LENSES",
				READING_GLASSES: "READING_GLASSES",
				EYE_DROPS: "EYE_DROPS",
				ACCESSORIES: "ACCESSORIES",
				INTRAOCULAR_LENSES: "INTRAOCULAR_LENSES",
				INJECTABLES: "INJECTABLES",
				MEDICATIONS: "MEDICATIONS",
				SURGICAL_CONSUMABLES: "SURGICAL_CONSUMABLES",
				SURGICAL_INSTRUMENTS: "SURGICAL_INSTRUMENTS",
				DIAGNOSTIC_SUPPLIES: "DIAGNOSTIC_SUPPLIES",
				SUNDRY: "SUNDRY",
				OTHER: "OTHER",
			},

			CATEGORY_LABELS: {
				FRAME: "Frames",
				SUNGLASSES: "Sunglasses",
				CONTACT_LENSES: "Contact lenses",
				READING_GLASSES: "Reading glasses",
				EYE_DROPS: "Eye drops",
				ACCESSORIES: "Accessories",
				INTRAOCULAR_LENSES: "Intraocular lenses",
				INJECTABLES: "Injectables",
				MEDICATIONS: "Medications",
				SURGICAL_CONSUMABLES: "Surgical consumables",
				SURGICAL_INSTRUMENTS: "Surgical instruments",
				DIAGNOSTIC_SUPPLIES: "Diagnostic supplies",
				SUNDRY: "Sundries",
				OTHER: "Other",
			},

			/**
			 * The categories offered for picking, in order, under the heading each sits in. SUNDRY is
			 * left out: it is retired.
			 */
			CATEGORY_GROUPS: [
				{ label: "Optical", categories: ["FRAME", "SUNGLASSES", "CONTACT_LENSES", "READING_GLASSES", "EYE_DROPS", "ACCESSORIES"] },
				{
					label: "Ophthalmology",
					categories: ["INTRAOCULAR_LENSES", "INJECTABLES", "MEDICATIONS", "SURGICAL_CONSUMABLES",
						"SURGICAL_INSTRUMENTS", "DIAGNOSTIC_SUPPLIES"],
				},
				{ label: "", categories: ["OTHER"] },
			],

			/**
			 * Creates an item with its variations and their starting stock. A contact lens item also
			 * describes the lens, and each variation is one box prescription.
			 * @param {Object} item - { name, category, other_category, price, variations: [{ color,
			 *   size, quantity, min_quantity, barcode, location_name, units_per_pack }] } - and for
			 *   CONTACT_LENSES { cl_maker, cl_material, cl_design, cl_modality, cl_catalogue_id }, each
			 *   variation with { cl_sphere, cl_cylinder, cl_axis, cl_add, cl_base_curve, cl_diameter }.
			 */
			createItem: function (item) {
				return $http({
					method: "POST",
					url: `${baseUri}/items`,
					headers: getHeaders(),
					data: item,
				});
			},

			/**
			 * Adds variations to an existing item - a contact lens's new box prescriptions, say - with
			 * their starting stock. Resolves to the whole item.
			 * @param {string} itemId
			 * @param {Array} variations - as createItem's variations.
			 */
			addVariations: function (itemId, variations) {
				return $http({
					method: "POST",
					url: `${baseUri}/items/${encodeURIComponent(itemId)}/variations`,
					headers: getHeaders(),
					data: variations,
				});
			},

			/**
			 * Gets one item with all of its variations, archived or not. A 404 when the practice
			 * has no such item.
			 * @param {string} itemId
			 */
			getItem: function (itemId) {
				return $http({
					method: "GET",
					url: `${baseUri}/items/${encodeURIComponent(itemId)}`,
					headers: getHeaders(),
				});
			},

			/**
			 * Lists a page of the active items in a category, or in every category, 25 at a time in name order, without
			 * their variations. Resolves to { items, page_indexes }; send page_indexes back for the
			 * next page. An empty page_indexes means that was the last page.
			 * @param {string} [category] - FRAME, SUNDRY or OTHER; leave out for every item.
			 * @param {string} [otherCategory] - For OTHER only: narrows to one category name, ignoring case.
			 * @param {Array} [pageIndexes] - The previous page's page_indexes; leave out for the first page.
			 * @param {number} [practiceId] - Another practice to list in.
			 * @param {string} [status] - STATUS.ARCHIVED for the archived items instead; leave out for the active ones.
			 */
			getItemsByCategory: function (category, otherCategory, pageIndexes, practiceId, status) {
				// No category lists every item, so the parameter is left out rather than sent empty.
				const params = {};
				if (category) {
					params.category = category;
				}
				if (otherCategory) {
					params.other_category = otherCategory;
				}
				if (status) {
					params.status = status;
				}
				return $http({
					method: "POST",
					url: `${baseUri}/items/query`,
					headers: getHeaders(practiceId),
					params: params,
					data: pageIndexes || [],
				});
			},

			/**
			 * Searches active items by partial match on the name or Other category name, ignoring
			 * case, 25 at a time in name order. Every word must appear, so "avi fr" finds "Aviator
			 * frame". No variations. Resolves to { items, page_indexes }, as getItemsByCategory.
			 * @param {string} term - At least 2 characters.
			 * @param {Array} [pageIndexes] - The previous page's page_indexes; leave out for the first page.
			 * @param {string} [status] - STATUS.ARCHIVED to search the archived items instead.
			 */
			searchItems: function (term, pageIndexes, status) {
				const params = { term: term };
				if (status) {
					params.status = status;
				}
				return $http({
					method: "POST",
					url: `${baseUri}/items/search`,
					headers: getHeaders(),
					params: params,
					data: pageIndexes || [],
				});
			},

			/**
			 * Adds stock to, or removes stock from, one variation.
			 * @param {number} qtyChange - Signed, in single units, never zero.
			 */
			adjustQuantity: function (itemId, variationId, qtyChange, notes) {
				return $http({
					method: "POST",
					url: `${baseUri}/items/${encodeURIComponent(itemId)}/variations/${encodeURIComponent(variationId)}/adjustments`,
					headers: getHeaders(),
					data: { qty_change: qtyChange, notes: notes || null },
				});
			},

			/**
			 * Replaces a variation's settings. A setting left out is cleared, so send them all - a
			 * contact lens box's prescription (cl_*) included.
			 * @param {Object} details - { min_quantity, barcode, location_name, units_per_pack, cl_* }
			 */
			updateVariationDetails: function (itemId, variationId, details) {
				return $http({
					method: "PUT",
					url: `${baseUri}/items/${encodeURIComponent(itemId)}/variations/${encodeURIComponent(variationId)}`,
					headers: getHeaders(),
					data: details,
				});
			},

			/** The reorder list: active items with a variation below its minimum quantity. */
			getItemsBelowMinimum: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/items/below-minimum`,
					headers: getHeaders(),
				});
			},

			/**
			 * Looks up a scanned barcode. Returns the matching items, each carrying only the matching
			 * variations - usually one - or an empty list.
			 * @param {string} barcode
			 * @param {number} [practiceId] - Another practice to look in.
			 */
			getItemsByBarcode: function (barcode, practiceId) {
				return $http({
					method: "GET",
					url: `${baseUri}/items/by-barcode`,
					headers: getHeaders(practiceId),
					params: { barcode: barcode },
				});
			},

			/**
			 * Moves stock from a variation in the session practice to a variation in another.
			 * @param {Object} move - { source_item_id, source_variation_id, target_practice_id,
			 *   target_item_id, target_variation_id, quantity, notes }
			 */
			moveStock: function (move) {
				return $http({
					method: "POST",
					url: `${baseUri}/moves`,
					headers: getHeaders(),
					data: move,
				});
			},

			/**
			 * The practice's backorders - what a sale couldn't take from stock, owed to a patient: every
			 * open one (PENDING or ORDERED), and those closed in the last `closedWithinDays` days (30 when
			 * left out), newest first.
			 */
			getBackorders: function (closedWithinDays) {
				return $http({
					method: "GET",
					url: `${baseUri}/backorders`,
					headers: getHeaders(),
					params: { closed_within_days: closedWithinDays === undefined ? null : closedWithinDays },
				});
			},

			/**
			 * Moves an open backorder on. Stock is never touched: what arrives goes straight to the patient.
			 * @param {string} status - "PENDING" or "ORDERED", either way; "ARRIVED" closes it.
			 * @param {string} [note] - Where it is up to, e.g. the supplier and order number. Left out keeps
			 *   the note; empty clears it.
			 */
			updateBackorderStatus: function (recordId, status, note) {
				return $http({
					method: "POST",
					url: `${baseUri}/backorders/${encodeURIComponent(recordId)}/status`,
					headers: getHeaders(),
					data: { status: status, note: note === undefined ? null : note },
				});
			},

			/**
			 * Archives an item. Refused while it has stock, unless that stock is written off.
			 * @param {boolean} [writeOffStock] - Write off any stock left rather than refuse.
			 */
			archiveItem: function (itemId, writeOffStock) {
				return $http({
					method: "POST",
					url: `${baseUri}/items/${encodeURIComponent(itemId)}/archive`,
					headers: getHeaders(),
					params: { write_off_stock: !!writeOffStock },
				});
			},

			/** Restores an archived item. Resolves to the item with its variations. */
			restoreItem: function (itemId) {
				return $http({
					method: "POST",
					url: `${baseUri}/items/${encodeURIComponent(itemId)}/restore`,
					headers: getHeaders(),
				});
			},

			/**
			 * Archives one variation of an item. Refused while it has stock, unless that stock is written
			 * off, and for the item's last active variation - archive the item instead.
			 * @param {boolean} [writeOffStock] - Write off any stock left rather than refuse.
			 */
			archiveVariation: function (itemId, variationId, writeOffStock) {
				return $http({
					method: "POST",
					url: `${baseUri}/items/${encodeURIComponent(itemId)}/variations/${encodeURIComponent(variationId)}/archive`,
					headers: getHeaders(),
					params: { write_off_stock: !!writeOffStock },
				});
			},

			/** Restores an archived variation. Refused while its item is archived. */
			restoreVariation: function (itemId, variationId) {
				return $http({
					method: "POST",
					url: `${baseUri}/items/${encodeURIComponent(itemId)}/variations/${encodeURIComponent(variationId)}/restore`,
					headers: getHeaders(),
				});
			},
		};

		return service;
	},
]);
